import { supabase } from "@/integrations/supabase/client";

export interface NormalizedAlternative {
  letter: string;
  text: string;
}

/**
 * Normaliza o enunciado para comparação de duplicatas.
 * Remove acentos, colapsa espaços, converte para minúsculas e usa primeiros 200 chars.
 */
export function normalizeStatement(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 200);
}

/**
 * Verifica se existem questões duplicadas no banco baseado no enunciado normalizado.
 * Retorna um Set com os enunciados normalizados que já existem.
 */
export async function fetchExistingNormalizedStatements(): Promise<Set<string>> {
  const allStatements: string[] = [];
  let from = 0;
  const batchSize = 1000;

  while (true) {
    const { data } = await supabase
      .from('questions')
      .select('statement')
      .range(from, from + batchSize - 1);

    if (!data || data.length === 0) break;

    allStatements.push(...data.map(q => normalizeStatement(q.statement)));

    if (data.length < batchSize) break;
    from += batchSize;
  }

  return new Set(allStatements);
}

/**
 * Verifica se um enunciado específico já existe no banco.
 * Usado para criação manual de questões.
 */
export async function checkSingleDuplicate(statement: string): Promise<{ isDuplicate: boolean; existingPreview?: string }> {
  const normalized = normalizeStatement(statement);
  if (normalized.length < 10) return { isDuplicate: false };

  // Search using first 100 chars to find candidates
  const searchTerm = normalized.substring(0, 100);

  const { data } = await supabase
    .from('questions')
    .select('statement')
    .ilike('statement', `%${searchTerm.replace(/%/g, '\\%').replace(/_/g, '\\_')}%`)
    .limit(5);

  if (!data || data.length === 0) return { isDuplicate: false };

  // Check normalized match
  const match = data.find(q => normalizeStatement(q.statement) === normalized);
  if (match) {
    return {
      isDuplicate: true,
      existingPreview: match.statement.substring(0, 120) + (match.statement.length > 120 ? '...' : '')
    };
  }

  return { isDuplicate: false };
}

/**
 * Normaliza alternativas de questões para um formato consistente.
 * Aceita múltiplos formatos de entrada e sempre retorna { letter, text }[]
 * 
 * Formatos aceitos:
 * - Array com { letter, text }
 * - Array com { id, text }
 * - Array com { key, text }
 * - Objeto { A: "texto", B: "texto" }
 * - String JSON
 */
export function normalizeAlternatives(
  alternatives: unknown
): NormalizedAlternative[] {
  try {
    if (!alternatives) return [];

    let parsed: unknown = alternatives;

    if (typeof alternatives === 'string') {
      try {
        parsed = JSON.parse(alternatives);
      } catch {
        return [];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed
        .map((alt) => {
          const a = (alt ?? {}) as Record<string, unknown>;
          return {
            letter: String(a.letter || a.id || a.key || ''),
            text: String(a.text || ''),
          };
        })
        .filter((alt) => alt.letter && alt.text)
        .sort((a, b) => a.letter.localeCompare(b.letter));
    }

    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed as Record<string, unknown>)
        .map(([letter, text]) => ({
          letter,
          text: typeof text === 'string' ? text : '',
        }))
        .filter((alt) => alt.letter && alt.text)
        .sort((a, b) => a.letter.localeCompare(b.letter));
    }

    return [];
  } catch (e) {
    console.warn('normalizeAlternatives failed', e);
    return [];
  }
}

/**
 * Detecta se um enunciado contém imagem embutida (Markdown ou HTML).
 * Usado para decidir se a questão deve ser resolvida em tela cheia.
 */
export function hasEmbeddedMedia(text: string | null | undefined): boolean {
  if (!text) return false;
  return /!\[[^\]]*\]\(/.test(text) || /<img\s/i.test(text);
}

/**
 * Remove resíduos de scraping do início do enunciado.
 * Exemplo: "$43\nSobre a utilização das reticências..." → "Sobre a utilização das reticências..."
 * O padrão `$NN` é um marcador de número de questão herdado de scraping antigo.
 * Não-destrutivo: se o texto não começa com o padrão, retorna inalterado.
 */
export function sanitizeQuestionStatement(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/^\s*\\*\$\d+\s*\n?/, '').trimStart();
}

/**
 * Detecta enunciados quebrados/truncados (resíduos de scraping).
 */
export function isStatementBroken(
  statement: string | null | undefined
): { broken: boolean; reason: string } {
  const raw = sanitizeQuestionStatement(statement);
  if (!raw || raw.trim().length === 0) return { broken: true, reason: 'empty' };

  const plain = raw.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  if (plain.length < 20) return { broken: true, reason: `too_short(${plain.length})` };

  const tail = raw.trim().slice(-40);
  if (/<[a-zA-Z][^>]*$/.test(tail)) return { broken: true, reason: 'unclosed_tag' };
  if (/(href|src)\s*=\s*["'\\]?[^"'>]*$/i.test(tail) && !tail.includes('>'))
    return { broken: true, reason: 'unclosed_attr' };
  if (/<\s*$/.test(tail)) return { broken: true, reason: 'trailing_lt' };

  const opens = (raw.match(/<[a-zA-Z][^>]*>/g) || []).length;
  const closes = (raw.match(/<\/[a-zA-Z][^>]*>/g) || []).length;
  const selfClosing = (raw.match(/<(br|img|hr|input)[^>]*\/?>/gi) || []).length;
  if (opens - selfClosing - closes > 2) return { broken: true, reason: 'unbalanced_tags' };

  return { broken: false, reason: '' };
}

/**
 * Reporta uma questão quebrada para revisão admin via question_error_reports.
 * Dedup por (user_id, question_id, reason='auto_broken_statement'). Silencioso em erro.
 */
export async function reportBrokenQuestion(
  questionId: string,
  detectorReason: string,
  statementPreview: string,
  moduleContext: string
): Promise<void> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return;

    const REASON = 'auto_broken_statement';

    const { data: existing } = await supabase
      .from('question_error_reports')
      .select('id')
      .eq('question_id', questionId)
      .eq('reason', REASON)
      .limit(1)
      .maybeSingle();

    if (existing?.id) return;

    await supabase.from('question_error_reports').insert({
      user_id: userId,
      question_id: questionId,
      reason: REASON,
      details: `${detectorReason} | ${statementPreview.slice(0, 200)}`,
      module_context: moduleContext,
      status: 'open',
    });
  } catch (e) {
    console.warn('reportBrokenQuestion failed', e);
  }
}

